import React from "react";
import { Users as UsersIcon, UserPlus, Check, Trash2, X } from "lucide-react";
import CustomSelect from "@/components/ui/CustomSelect";
import { FLIGHT_ROLES } from "@/lib/newTripConstants";

// Orden de mando para mostrar la tripulación: PIC arriba, luego SIC, y de ahí
// el resto. Los no seleccionados quedan por debajo, conservando el orden del
// roster (sort estable).
const FLIGHT_ROLE_ORDER = { PIC: 0, SIC: 1, DH: 2, OBS: 3 };
const flightRoleRank = (role) => FLIGHT_ROLE_ORDER[role] ?? 50;

// Autocomplete for saved temp-crew, in its own memo'd component so a keystroke
// in one external-crew row doesn't re-run the filter for every other row. Born
// from a UX complaint: the dropdown had no way to be dismissed without picking
// an option. It now closes on Escape, on a click outside, or via the X button —
// and stays closed for that exact typed name (reappears once the name changes).
const CrewSuggestions = React.memo(function CrewSuggestions({
  first,
  middle,
  last,
  temporaryCrew,
  onSelect,
}) {
  const typedFirst = (first || "").toLowerCase().trim();
  const typedLast = (last || "").toLowerCase().trim();
  const typedMiddle = (middle || "").toLowerCase().trim();
  const typedKey = `${typedFirst}|${typedMiddle}|${typedLast}`;
  const [dismissedKey, setDismissedKey] = React.useState(null);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setDismissedKey(typedKey);
    };
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setDismissedKey(typedKey);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [typedKey]);
  if (!typedFirst && !typedLast) return null;
  if (dismissedKey === typedKey) return null;
  // Match saved temp-crew by either name part the user is typing, so a
  // half-typed first OR last name still surfaces it.
  const suggestions = temporaryCrew
    .filter((tc) => {
      const fn = (tc.first_name || "").toLowerCase();
      const ln = (tc.last_name || "").toLowerCase();
      const full = (tc.name || "").toLowerCase();
      const exact = fn === typedFirst && ln === typedLast;
      if (exact) return false;
      return (
        (typedFirst && (fn.includes(typedFirst) || full.includes(typedFirst))) ||
        (typedLast && (ln.includes(typedLast) || full.includes(typedLast)))
      );
    })
    .slice(0, 5);
  if (suggestions.length === 0) return null;
  return (
    <div
      ref={ref}
      className="absolute top-full left-0 right-0 mt-1 bg-[#1e293b] border border-slate-700 rounded-lg shadow-xl z-10 max-h-32 overflow-y-auto"
    >
      <button
        type="button"
        onClick={() => setDismissedKey(typedKey)}
        className="absolute top-1 right-1 z-10 p-0.5 text-slate-500 hover:text-white"
        aria-label="Cerrar sugerencias"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      {suggestions.map((tc) => (
        <button
          key={tc.id}
          type="button"
          onClick={() => onSelect(tc)}
          className="w-full px-3 py-2 text-left text-sm text-white hover:bg-slate-700 flex items-center gap-2"
        >
          <Check className="w-3 h-3 text-green-400" />
          <span className="flex-1">{tc.name}</span>
          {tc.phone && <span className="text-[10px] text-slate-400">{tc.phone}</span>}
        </button>
      ))}
    </div>
  );
});

function CrewSection({
  leg,
  legIndex,
  assignedCrew,
  crewLoadError,
  temporaryCrew,
  extCrewPickerLeg,
  setExtCrewPickerLeg,
  openExtCrewPicker,
  addExternalCrew,
  addExternalCrewFromTemp,
  updateExternalCrew,
  setExternalCrewFields,
  removeExternalCrew,
  toggleCrewMember,
  updateCrewFlightRole,
  isCrewSelected,
  t,
}) {
  // Reordenar la lista de tripulación del avión para que el PIC quede arriba
  // del SIC (y los seleccionados arriba de los no elegidos). El sort es estable,
  // así que entre roles iguales se respeta el orden original del roster.
  const orderedAssignedCrew = React.useMemo(() => {
    const crewList = leg.crew || [];
    const rankFor = (id) => {
      const member = crewList.find((c) => c.user_id === id);
      return member ? flightRoleRank(member.flight_role) : 100;
    };
    return [...assignedCrew].sort((a, b) => rankFor(a.id) - rankFor(b.id));
  }, [assignedCrew, leg.crew]);

  // Misma lógica para el resumen (interno + externo): PIC primero, SIC después.
  const orderedSummaryCrew = React.useMemo(
    () =>
      [...(leg.crew || []), ...(leg.external_crew || [])].sort(
        (a, b) => flightRoleRank(a.flight_role) - flightRoleRank(b.flight_role)
      ),
    [leg.crew, leg.external_crew]
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs text-slate-400 flex items-center gap-1">
          <UsersIcon className="w-3.5 h-3.5" /> {t("newTrip.crewAssignmentRequired")}
        </label>
        <button
          type="button"
          onClick={() => openExtCrewPicker(legIndex)}
          className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
        >
          <UserPlus className="w-3.5 h-3.5" /> {t("newTrip.addExternal")}
        </button>
      </div>

      {/* Temporary-crew picker: aparece cuando el avion tiene
          externos guardados. Lista los que aun no estan en
          el leg + opcion de agregar uno manualmente. */}
      {extCrewPickerLeg === legIndex &&
        (() => {
          const usedNames = new Set(
            (leg.external_crew || [])
              .map((ec) => (ec.name || "").toLowerCase().trim())
              .filter(Boolean)
          );
          const availableTemp = (temporaryCrew || []).filter(
            (tc) => tc.name && !usedNames.has(tc.name.toLowerCase().trim())
          );
          return (
            <div className="mb-3 p-3 bg-[#0f172a] border border-slate-700 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-slate-400 uppercase tracking-wide">
                  {t("newTrip.pickFromAircraft")}
                </p>
                <button
                  type="button"
                  onClick={() => setExtCrewPickerLeg(null)}
                  className="text-[10px] text-slate-400 hover:text-slate-300"
                >
                  {t("common.cancel")}
                </button>
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {availableTemp.map((tc) => (
                  <button
                    key={tc.id}
                    type="button"
                    onClick={() => {
                      addExternalCrewFromTemp(legIndex, tc);
                      setExtCrewPickerLeg(null);
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-white bg-slate-800/50 hover:bg-slate-700 border border-slate-700 rounded-lg flex items-center gap-2"
                  >
                    <UserPlus className="w-3.5 h-3.5 text-blue-400" />
                    <span className="flex-1 truncate">{tc.name}</span>
                    {tc.phone && <span className="text-[10px] text-slate-400">{tc.phone}</span>}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => {
                  addExternalCrew(legIndex);
                  setExtCrewPickerLeg(null);
                }}
                className="w-full px-3 py-2 text-sm text-blue-400 hover:text-blue-300 border border-dashed border-slate-700 hover:border-blue-500/50 rounded-lg flex items-center justify-center gap-2"
              >
                <UserPlus className="w-3.5 h-3.5" /> {t("newTrip.addManually")}
              </button>
            </div>
          );
        })()}

      {/* Assigned Aircraft Crew */}
      {assignedCrew.length > 0 && (
        <div className="space-y-2 mb-3">
          <p className="text-[10px] text-slate-400 uppercase tracking-wide">
            {t("newTrip.aircraftCrewTitle")}
          </p>
          {orderedAssignedCrew.map((crew) => {
            const selected = isCrewSelected(legIndex, crew.id);
            const selectedCrewData = (leg.crew || []).find((c) => c.user_id === crew.id);
            return (
              <div key={crew.id} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => toggleCrewMember(legIndex, crew)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-all flex items-center gap-2 ${
                    selected
                      ? "bg-blue-500/20 border-blue-500/50 text-blue-400"
                      : "bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600"
                  }`}
                >
                  {selected && <Check className="w-4 h-4" />}
                  <span className="flex-1 text-left">{crew.full_name}</span>
                  <span className="text-[10px] opacity-70 bg-slate-700/50 px-1.5 py-0.5 rounded">
                    {crew.role}
                  </span>
                </button>
                {selected && (
                  <CustomSelect
                    value={selectedCrewData?.flight_role || "PIC"}
                    onChange={(e) => updateCrewFlightRole(legIndex, crew.id, e.target.value)}
                    className="w-28 bg-[#0f172a] border border-slate-700 rounded-lg px-2 py-2 text-white text-xs focus:outline-none focus:border-[#0052FF]"
                  >
                    {FLIGHT_ROLES.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label} - {role.description}
                      </option>
                    ))}
                  </CustomSelect>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Estado vacío vs error — bug 11 audit: antes "Sin tripulación asignada"
          se mostraba aunque el endpoint hubiera fallado, dando una falsa
          confirmación de "no hay crew" cuando en realidad no se pudo cargar. */}
      {assignedCrew.length === 0 && (leg.external_crew || []).length === 0 && (
        <p className={`text-xs italic mb-2 ${crewLoadError ? "text-amber-400" : "text-slate-400"}`}>
          {crewLoadError ? t("common.crewLoadError") : t("common.noCrewAssigned")}
        </p>
      )}

      {/* External Crew */}
      {(leg.external_crew || []).length > 0 && (
        <div className="space-y-2 mt-3">
          <p className="text-[10px] text-slate-400 uppercase tracking-wide">
            {t("newTrip.externalCrewTitle")}
          </p>
          {(leg.external_crew || []).map((ext, extIndex) => {
            // items-start so the role select + trash align with the FIRST
            // (name) row of the stacked field column, not its vertical center.
            return (
              <div key={ext.id} className="flex items-start gap-2">
                <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                  {/* Name row: first / middle / last (autocomplete anchored here). */}
                  <div className="relative">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={ext.first_name || ""}
                        onChange={(e) =>
                          updateExternalCrew(legIndex, extIndex, "first_name", e.target.value)
                        }
                        placeholder={t("common.firstName")}
                        className="flex-1 min-w-0 bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0052FF]"
                      />
                      <input
                        type="text"
                        value={ext.middle_name || ""}
                        onChange={(e) =>
                          updateExternalCrew(legIndex, extIndex, "middle_name", e.target.value)
                        }
                        placeholder={t("common.middleName")}
                        className="flex-1 min-w-0 bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0052FF]"
                      />
                      <input
                        type="text"
                        value={ext.last_name || ""}
                        onChange={(e) =>
                          updateExternalCrew(legIndex, extIndex, "last_name", e.target.value)
                        }
                        placeholder={t("common.lastName")}
                        className="flex-1 min-w-0 bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0052FF]"
                      />
                    </div>
                    <CrewSuggestions
                      first={ext.first_name}
                      middle={ext.middle_name}
                      last={ext.last_name}
                      temporaryCrew={temporaryCrew}
                      onSelect={(tc) =>
                        setExternalCrewFields(legIndex, extIndex, {
                          first_name: tc.first_name || "",
                          middle_name: tc.middle_name || "",
                          last_name: tc.last_name || "",
                          phone: tc.phone || "",
                          email: tc.email || "",
                        })
                      }
                    />
                  </div>
                  {/* Contact row: phone + email (optional, for ad-hoc external crew). */}
                  <div className="flex gap-2">
                    <input
                      type="tel"
                      value={ext.phone || ""}
                      onChange={(e) =>
                        updateExternalCrew(legIndex, extIndex, "phone", e.target.value)
                      }
                      placeholder={t("common.phone")}
                      className="flex-1 min-w-0 bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0052FF]"
                    />
                    <input
                      type="email"
                      value={ext.email || ""}
                      onChange={(e) =>
                        updateExternalCrew(legIndex, extIndex, "email", e.target.value)
                      }
                      placeholder={t("common.email")}
                      className="flex-1 min-w-0 bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0052FF]"
                    />
                  </div>
                </div>
                {/* Mobile: w-16 con solo el codigo (PIC/SIC).
                    Desktop: w-28 con descripcion completa. El
                    nombre del piloto necesita protagonismo;
                    el rol es secundario. */}
                <CustomSelect
                  value={ext.flight_role}
                  onChange={(e) =>
                    updateExternalCrew(legIndex, extIndex, "flight_role", e.target.value)
                  }
                  className="w-16 sm:w-28 flex-shrink-0 bg-[#0f172a] border border-slate-700 rounded-lg px-2 py-2 text-white text-xs focus:outline-none focus:border-[#0052FF]"
                >
                  {FLIGHT_ROLES.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </CustomSelect>
                <button
                  type="button"
                  onClick={() => removeExternalCrew(legIndex, extIndex)}
                  className="w-9 h-9 flex items-center justify-center flex-shrink-0 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Crew summary */}
      {((leg.crew || []).length > 0 || (leg.external_crew || []).length > 0) && (
        <div className="mt-2 p-2 bg-slate-800/30 rounded-lg">
          <p className="text-[10px] text-slate-400">
            <span className="font-medium text-white">
              {t("newTrip.crewCount", {
                count: (leg.crew || []).length + (leg.external_crew || []).length,
              })}
            </span>{" "}
            {orderedSummaryCrew.map((c, i) => (
              <span key={i} className="text-slate-300">
                {c.name} <span className="text-blue-400">({c.flight_role || "PIC"})</span>
                {i < orderedSummaryCrew.length - 1 ? ", " : ""}
              </span>
            ))}
          </p>
        </div>
      )}
    </div>
  );
}

// Memo barrier — same reason as PassengersSection: keystrokes in another leg
// shouldn't churn the crew picker of every leg.
export default React.memo(CrewSection);
